import { newId, now } from '../model/ids';
import {
  A4, FORMAT_ID,
  type DeviceInfo, type Exported, type HoverSegment, type Notebook, type Page, type Stroke,
  type Template, type UUID,
} from '../model/types';
import type { RecordSet } from '../format/inkfmt';
import { openInkDb, RECORD_STORES, type InkDb, type JournalEntry, type RecordStore } from './db';

type StoreValue = { notebooks: Notebook; pages: Page; strokes: Stroke; hover: HoverSegment };
type Unrevved<T> = Omit<T, 'rev'>;

const REV_KEY = 'revCounter';

export interface ImportResult {
  added: number;
  updated: number;
  skipped: number;
}

/** Writes in one transaction; every put gets the next rev from the device counter. */
interface Writer {
  get<S extends RecordStore>(store: S, id: UUID): Promise<StoreValue[S] | undefined>;
  put<S extends RecordStore>(store: S, record: Unrevved<StoreValue[S]>): StoreValue[S];
}

export class Repo {
  constructor(readonly db: InkDb) {}

  static async open(name?: string): Promise<Repo> {
    return new Repo(await openInkDb(name));
  }

  close(): void {
    this.db.close();
  }

  // ---------- core write path ----------

  private async write<T>(stores: readonly RecordStore[], fn: (w: Writer) => Promise<T> | T): Promise<T> {
    const tx = this.db.transaction([...stores, 'settings'] as (RecordStore | 'settings')[], 'readwrite');
    const settings = tx.objectStore('settings');
    let counter = ((await settings.get(REV_KEY))?.value as number | undefined) ?? 0;
    const start = counter;
    // idb's per-store typing doesn't survive a union of store names.
    const store = (s: RecordStore) => tx.objectStore(s) as unknown as {
      get(id: string): Promise<unknown>;
      put(v: unknown): Promise<unknown>;
    };
    const w: Writer = {
      get: (s, id) => store(s).get(id) as Promise<never>,
      put: (s, record) => {
        const r = { ...record, rev: ++counter } as StoreValue[typeof s];
        store(s).put(r).catch(() => { /* surfaced via tx.done */ });
        return r;
      },
    };
    const result = await fn(w);
    if (counter !== start) void settings.put({ key: REV_KEY, value: counter });
    await tx.done;
    return result;
  }

  async currentRev(): Promise<number> {
    return ((await this.db.get('settings', REV_KEY))?.value as number | undefined) ?? 0;
  }

  // ---------- settings ----------

  async getSetting<T>(key: string): Promise<T | undefined> {
    return (await this.db.get('settings', key))?.value as T | undefined;
  }

  async setSetting(key: string, value: unknown): Promise<void> {
    if (key === REV_KEY) throw new Error('rev counter is managed by the repo');
    await this.db.put('settings', { key, value });
  }

  // ---------- notebooks ----------

  async listNotebooks(includeDeleted = false): Promise<Notebook[]> {
    const all = await this.db.getAll('notebooks');
    return all.filter((n) => includeDeleted || n.deletedAt === undefined).sort((a, b) => b.updatedAt - a.updatedAt);
  }

  getNotebook(id: UUID): Promise<Notebook | undefined> {
    return this.db.get('notebooks', id);
  }

  async createNotebook(title: string, device: DeviceInfo, template: Template = 'ruled'): Promise<{ notebook: Notebook; page: Page }> {
    const t = now();
    return this.write(['notebooks', 'pages'], (w) => {
      const notebook = w.put('notebooks', {
        id: newId(), createdAt: t, updatedAt: t,
        format: FORMAT_ID, title, devices: { [device.id]: device }, defaultTemplate: template,
      });
      const page = w.put('pages', {
        id: newId(), createdAt: t, updatedAt: t,
        notebookId: notebook.id, index: 0, template, size: { ...A4 },
      });
      return { notebook, page };
    });
  }

  renameNotebook(id: UUID, title: string): Promise<Notebook> {
    return this.updateRecord('notebooks', id, (n) => ({ ...n, title }));
  }

  /** Soft delete. Pages and strokes are kept untouched. */
  deleteNotebook(id: UUID): Promise<Notebook> {
    return this.updateRecord('notebooks', id, (n) => ({ ...n, deletedAt: now() }));
  }

  restoreNotebook(id: UUID): Promise<Notebook> {
    return this.updateRecord('notebooks', id, ({ deletedAt: _d, ...n }) => n);
  }

  /** Adds this device's info to the notebook if it isn't there yet. */
  async ensureDevice(notebookId: UUID, device: DeviceInfo): Promise<void> {
    const nb = await this.getNotebook(notebookId);
    if (!nb || nb.devices[device.id]) return;
    await this.updateRecord('notebooks', notebookId, (n) => ({ ...n, devices: { ...n.devices, [device.id]: device } }));
  }

  private async updateRecord<S extends RecordStore>(
    store: S, id: UUID, fn: (r: Unrevved<StoreValue[S]>) => Unrevved<StoreValue[S]>,
  ): Promise<StoreValue[S]> {
    return this.write([store], async (w) => {
      const cur = await w.get(store, id);
      if (!cur) throw new Error(`${store}/${id} not found`);
      const { rev: _rev, ...rest } = cur;
      return w.put(store, { ...fn(rest as Unrevved<StoreValue[S]>), updatedAt: now() });
    });
  }

  // ---------- pages ----------

  async listPages(notebookId: UUID, includeDeleted = false): Promise<Page[]> {
    const pages = await this.db.getAllFromIndex('pages', 'notebookId', notebookId);
    return pages.filter((p) => includeDeleted || p.deletedAt === undefined).sort((a, b) => a.index - b.index);
  }

  /** Appends a page at the end of the notebook. */
  async addPage(notebookId: UUID, template?: Template): Promise<Page> {
    const pages = await this.listPages(notebookId, true);
    const nb = await this.getNotebook(notebookId);
    if (!nb) throw new Error(`notebook ${notebookId} not found`);
    const t = now();
    return this.write(['pages', 'notebooks'], async (w) => {
      const page = w.put('pages', {
        id: newId(), createdAt: t, updatedAt: t, notebookId,
        index: pages.reduce((m, p) => Math.max(m, p.index + 1), 0),
        template: template ?? nb.defaultTemplate, size: { ...A4 },
      });
      await this.touch(w, notebookId);
      return page;
    });
  }

  /** All strokes for a notebook (deleted included; the engine skips those). */
  listNotebookStrokes(notebookId: UUID): Promise<Stroke[]> {
    return this.db.getAllFromIndex('strokes', 'notebookId', notebookId);
  }

  // ---------- strokes & hover ----------

  /** Inserts or replaces a stroke and bumps the notebook's updatedAt. */
  async putStroke(stroke: Unrevved<Stroke>): Promise<Stroke> {
    return this.write(['strokes', 'notebooks'], async (w) => {
      const saved = w.put('strokes', stroke);
      await this.touch(w, stroke.notebookId);
      return saved;
    });
  }

  /** Writes several stroke records at once (used by editing commands). */
  async putStrokes(strokes: Unrevved<Stroke>[]): Promise<Stroke[]> {
    if (strokes.length === 0) return [];
    return this.write(['strokes', 'notebooks'], async (w) => {
      const saved = strokes.map((s) => w.put('strokes', s));
      for (const nbId of new Set(strokes.map((s) => s.notebookId))) await this.touch(w, nbId);
      return saved;
    });
  }

  listStrokes(pageId: UUID): Promise<Stroke[]> {
    return this.db.getAllFromIndex('strokes', 'pageId', pageId);
  }

  async putHover(seg: Unrevved<HoverSegment>): Promise<HoverSegment> {
    return this.write(['hover'], (w) => w.put('hover', seg));
  }

  listHover(pageId: UUID): Promise<HoverSegment[]> {
    return this.db.getAllFromIndex('hover', 'pageId', pageId);
  }

  private async touch(w: Writer, notebookId: UUID): Promise<void> {
    const nb = await w.get('notebooks', notebookId);
    if (!nb) return;
    const { rev: _rev, ...rest } = nb;
    w.put('notebooks', { ...rest, updatedAt: now() });
  }

  // ---------- journal (in-progress stroke crash safety) ----------

  async putJournal(stroke: Unrevved<Stroke>): Promise<void> {
    const entry: JournalEntry = { id: stroke.id, stroke, savedAt: now() };
    await this.db.put('journal', entry);
  }

  async clearJournal(id: UUID): Promise<void> {
    await this.db.delete('journal', id);
  }

  /** Moves any leftover journal entries into the strokes store. Returns how many were recovered. */
  async recoverJournal(): Promise<number> {
    const entries = await this.db.getAll('journal');
    for (const e of entries) {
      const exists = await this.db.get('strokes', e.id);
      if (!exists && e.stroke.points.length > 0) await this.putStroke(e.stroke);
      await this.clearJournal(e.id);
    }
    return entries.length;
  }

  // ---------- bulk / sync ----------

  /** Every record in every store, deleted ones included. */
  async exportAll(): Promise<{ notebooks: Notebook[]; pages: Page[]; strokes: Stroke[]; hover: HoverSegment[] }> {
    const tx = this.db.transaction(RECORD_STORES, 'readonly');
    const [notebooks, pages, strokes, hover] = await Promise.all([
      tx.objectStore('notebooks').getAll(),
      tx.objectStore('pages').getAll(),
      tx.objectStore('strokes').getAll(),
      tx.objectStore('hover').getAll(),
    ]);
    return { notebooks, pages, strokes, hover };
  }

  /** Everything for one notebook, deleted records included. */
  async exportNotebook(id: UUID) {
    const nb = await this.getNotebook(id);
    if (!nb) throw new Error(`notebook ${id} not found`);
    const [pages, strokes, hover] = await Promise.all([
      this.db.getAllFromIndex('pages', 'notebookId', id),
      this.db.getAllFromIndex('strokes', 'notebookId', id),
      this.db.getAllFromIndex('hover', 'notebookId', id),
    ]);
    return { notebooks: [nb], pages, strokes, hover };
  }

  /** Records whose rev is greater than `rev`, per store. */
  async changesSince(rev: number) {
    const range = IDBKeyRange.lowerBound(rev, true);
    const tx = this.db.transaction(RECORD_STORES, 'readonly');
    const [notebooks, pages, strokes, hover] = await Promise.all([
      tx.objectStore('notebooks').index('rev').getAll(range),
      tx.objectStore('pages').index('rev').getAll(range),
      tx.objectStore('strokes').index('rev').getAll(range),
      tx.objectStore('hover').index('rev').getAll(range),
    ]);
    return { notebooks, pages, strokes, hover };
  }

  /**
   * Merges records by id. A record is written if it is new here, or if its
   * updatedAt is newer than the local copy. Notebook device maps are unioned.
   */
  async importRecords(set: RecordSet): Promise<ImportResult> {
    const res: ImportResult = { added: 0, updated: 0, skipped: 0 };
    await this.write(RECORD_STORES, async (w) => {
      const merge = async <S extends RecordStore>(store: S, items: Exported<StoreValue[S]>[]) => {
        for (const item of items) {
          const cur = await w.get(store, item.id);
          if (!cur) {
            w.put(store, item as Unrevved<StoreValue[S]>);
            res.added++;
          } else if (item.updatedAt > cur.updatedAt) {
            let next = item as Unrevved<StoreValue[S]>;
            if (store === 'notebooks') {
              const n = next as Unrevved<Notebook>;
              next = { ...n, devices: { ...(cur as Notebook).devices, ...n.devices } } as unknown as typeof next;
            }
            w.put(store, next);
            res.updated++;
          } else {
            res.skipped++;
          }
        }
      };
      await merge('notebooks', set.notebooks);
      await merge('pages', set.pages);
      await merge('strokes', set.strokes);
      await merge('hover', set.hover);
    });
    return res;
  }
}
